// Store module for managing drag and drop upload functionality
import fileUpload, { SUPPORTED_FILE_TYPES } from '@/utils/fileUpload';

const state = {
  // Files that were dropped and need to be uploaded
  droppedFiles: [],
  // Whether the upload dialog should be shown
  showUploadDialog: false,
  // Source of the upload (drag-drop, manual, etc.)
  uploadSource: null,
  // Whether global drag and drop is enabled
  isDragDropEnabled: true,
  // Loading state for delayed dialog opening
  isProcessingDrop: false
};

const getters = {
  droppedFiles: state => state.droppedFiles,
  showUploadDialog: state => state.showUploadDialog,
  uploadSource: state => state.uploadSource,
  hasDroppedFiles: state => state.droppedFiles.length > 0,
  isDragDropEnabled: state => state.isDragDropEnabled,
  isProcessingDrop: state => state.isProcessingDrop
};

const mutations = {
  SET_DROPPED_FILES(state, files) {
    state.droppedFiles = files;
  },

  ADD_DROPPED_FILES(state, files) {
    state.droppedFiles = [...state.droppedFiles, ...files];
  },

  CLEAR_DROPPED_FILES(state) {
    // Clean up any object URLs to prevent memory leaks
    state.droppedFiles.forEach(file => {
      if (file.preview) {
        URL.revokeObjectURL(file.preview);
      }
    });
    state.droppedFiles = [];
  },

  SET_SHOW_UPLOAD_DIALOG(state, show) {
    state.showUploadDialog = show;
  },

  SET_UPLOAD_SOURCE(state, source) {
    state.uploadSource = source;
  },

  SET_DRAG_DROP_ENABLED(state, enabled) {
    state.isDragDropEnabled = enabled;
  },

  SET_PROCESSING_DROP(state, processing) {
    state.isProcessingDrop = processing;
  }
};

const actions = {
  // Handle files dropped from drag and drop
  async handleFileDrop({ commit, state }, { files, source = 'drag-drop' }) {
    // Check if drag and drop is enabled
    if (!state.isDragDropEnabled) {
      console.warn('Drag and drop is currently disabled');
      return;
    }

    // Filter for supported file types only
    const validFiles = files.filter(file => SUPPORTED_FILE_TYPES.includes(file.type));

    if (validFiles.length === 0) {
      // No valid files found - notify user
      throw new Error('No supported file types found. Please use JPEG, JPG, PNG, MP4, MOV, PDF, DOC, or DOCX files only.');
    }

    // Notify if some files were filtered out
    if (validFiles.length < files.length) {
      const skippedCount = files.length - validFiles.length;
      console.warn(`Skipped ${skippedCount} unsupported file(s). Only JPEG, JPG, PNG, MP4, MOV, PDF, DOC, and DOCX files are supported.`);
    }

    // Start processing state and disable drag/drop immediately
    commit('SET_PROCESSING_DROP', true);
    commit('SET_DRAG_DROP_ENABLED', false);

    // Process files and create preview URLs (including video and PDF thumbnails)
    const processedFiles = await Promise.all(
      validFiles.map(async file => {
        let preview = null;

        if (file.type.startsWith('image/')) {
          // For images, create object URL directly
          preview = URL.createObjectURL(file);
        } else if (file.type.startsWith('video/')) {
          // For videos, generate thumbnail using the helper function
          try {
            preview = await fileUpload.generateVideoThumbnail(file);
          } catch (error) {
            preview = null; // Fallback to no preview
          }
        } else if (file.type === 'application/pdf') {
          // For PDFs, don't generate thumbnail on client - server will handle it
          preview = null;
        }

        return {
          file,
          preview,
          name: file.name,
          size: file.size,
          type: file.type
        };
      })
    );

    // Add the processed files
    commit('ADD_DROPPED_FILES', processedFiles);
    commit('SET_UPLOAD_SOURCE', source);

    // Wait for 1.5 seconds to let all drag animations settle before opening dialog
    setTimeout(() => {
      commit('SET_SHOW_UPLOAD_DIALOG', true);
      commit('SET_PROCESSING_DROP', false);
    }, 1500);
  },

  // Open upload dialog manually (from button click, etc.)
  openUploadDialog({ commit }, source = 'manual') {
    commit('SET_UPLOAD_SOURCE', source);
    commit('SET_SHOW_UPLOAD_DIALOG', true);
    // Disable drag and drop when dialog opens manually too
    commit('SET_DRAG_DROP_ENABLED', false);
  },

  // Close upload dialog and clean up
  closeUploadDialog({ commit }) {
    commit('SET_SHOW_UPLOAD_DIALOG', false);
    commit('SET_PROCESSING_DROP', false); // Reset processing state
    // Small delay before clearing files to allow dialog close animation
    setTimeout(() => {
      commit('CLEAR_DROPPED_FILES');
      commit('SET_UPLOAD_SOURCE', null);
      // Re-enable drag and drop when dialog closes
      commit('SET_DRAG_DROP_ENABLED', true);
    }, 300);
  },

  // Clear dropped files without closing dialog (for when user removes files manually)
  clearDroppedFiles({ commit }) {
    commit('CLEAR_DROPPED_FILES');
  },

  // Add more files to existing dropped files
  async addFilesToDrop({ commit, state }, files) {
    // Check if drag and drop is enabled
    if (!state.isDragDropEnabled) {
      console.warn('Drag and drop is currently disabled');
      return;
    }

    // Filter for supported file types only
    const validFiles = files.filter(file => SUPPORTED_FILE_TYPES.includes(file.type));

    if (validFiles.length === 0) {
      // No valid files found
      return;
    }

    const processedFiles = await Promise.all(
      validFiles.map(async file => {
        let preview = null;

        if (file.type.startsWith('image/')) {
          // For images, create object URL directly
          preview = URL.createObjectURL(file);
        } else if (file.type.startsWith('video/')) {
          // For videos, generate thumbnail using the helper function
          try {
            preview = await fileUpload.generateVideoThumbnail(file);
          } catch (error) {
            preview = null; // Fallback to no preview
          }
        } else if (file.type === 'application/pdf') {
          // For PDFs, don't generate thumbnail on client - server will handle it
          preview = null;
        }

        return {
          file,
          preview,
          name: file.name,
          size: file.size,
          type: file.type
        };
      })
    );

    commit('ADD_DROPPED_FILES', processedFiles);
  },

  // Manually disable drag and drop
  disableDragDrop({ commit }) {
    commit('SET_DRAG_DROP_ENABLED', false);
  },

  // Manually enable drag and drop
  enableDragDrop({ commit }) {
    commit('SET_DRAG_DROP_ENABLED', true);
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
